import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import {
  LEASE_STATUS,
  fetchLease,
  formatLeaseTimestamp,
  getLeaseStatusInfo,
  isPdfUrl,
  signLease,
} from '../../Services/leaseService';
import '../../Components/Lease/LeaseAgreement.css';

/**
 * Tenant-side lease agreement: review the lease the landlord sent and sign it
 * with a typed legal name. Renders nothing until a lease has actually been sent.
 *
 * `onSignedChange` is called with true once this card knows the lease is signed,
 * so the page can unlock the rent subscription right after signing instead of
 * waiting for a reload of the property. It never reports false — the property's
 * own `lease_signed` flag stays the source of truth when no lease row loads.
 */
const LeaseSignCard = ({ propertyId, tenantName = '', onSignedChange }) => {
  const [lease, setLease] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [hasAgreed, setHasAgreed] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSigning, setIsSigning] = useState(false);

  const loadLease = useCallback(async ({ silent = false } = {}) => {
    if (!propertyId) {
      setIsLoading(false);
      return;
    }
    if (!silent) setIsLoading(true);
    try {
      // Tenant is identified by the auth token, so property_id is enough here.
      const row = await fetchLease({ propertyId });
      setLease(row);
    } catch (error) {
      const statusCode = error?.response?.status ?? error?.response?.data?.statusCode;
      if (statusCode === 404) {
        setLease(null);
      } else {
        console.error('Error loading lease agreement:', error);
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    loadLease();
  }, [loadLease]);

  const statusInfo = lease ? getLeaseStatusInfo(lease.status) : null;
  const isSigned = statusInfo?.key === LEASE_STATUS.SIGNED;
  const isAwaitingSignature = statusInfo?.key === LEASE_STATUS.SENT;
  const documentUrl = lease?.signedDocumentUrl || lease?.documentUrl || null;

  useEffect(() => {
    if (isSigned && onSignedChange) onSignedChange(true);
  }, [isSigned, onSignedChange]);

  const openModal = () => {
    setSignatureName(tenantName || '');
    setHasAgreed(false);
    setFormError('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSigning) return;
    setIsModalOpen(false);
    setFormError('');
  };

  const handleOpenDocument = () => {
    if (!documentUrl) return;
    window.open(documentUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSign = async () => {
    const trimmedName = signatureName.trim();
    if (!trimmedName) {
      setFormError('Please type your full legal name to sign.');
      return;
    }
    if (!hasAgreed) {
      setFormError('Please confirm you have read and agree to the lease terms.');
      return;
    }
    setFormError('');
    setIsSigning(true);
    try {
      const result = await signLease({
        leaseId: lease?.id,
        propertyId,
        signatureName: trimmedName,
      });
      toast.success(result?.message || 'Lease signed successfully.');
      setIsModalOpen(false);
      if (result?.lease) setLease(result.lease);
      else await loadLease({ silent: true });
    } catch (error) {
      console.error('Error signing lease agreement:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to sign the lease. Please try again.');
    } finally {
      setIsSigning(false);
    }
  };

  // Nothing to show until the landlord actually sends the lease.
  if (isLoading || !lease || (!isAwaitingSignature && !isSigned)) return null;

  return (
    <div className="screening-status-section">
      <h3 className="screening-status-title">Lease Agreement</h3>
      <div className="lease-card-body" style={{ padding: 0 }}>
        <div className="lease-document-item">
          <div className="lease-document-info">
            <span className="lease-document-title">{lease.title}</span>
            {lease.fileName ? <span className="lease-document-meta">{lease.fileName}</span> : null}
          </div>
          <div className="lease-document-actions">
            <span className={`lease-badge lease-badge--${statusInfo.variant}`}>{statusInfo.label}</span>
          </div>
        </div>

        {isSigned ? (
          <>
            <dl className="lease-detail-grid">
              {lease.signedAt ? (
                <div className="lease-detail-item">
                  <dt className="lease-detail-label">Signed on</dt>
                  <dd className="lease-detail-value">{formatLeaseTimestamp(lease.signedAt)}</dd>
                </div>
              ) : null}
              {lease.signedBy ? (
                <div className="lease-detail-item">
                  <dt className="lease-detail-label">Signed by</dt>
                  <dd className="lease-detail-value">{String(lease.signedBy)}</dd>
                </div>
              ) : null}
            </dl>
            <div className="lease-actions">
              <button
                type="button"
                className="lease-btn lease-btn--secondary"
                onClick={handleOpenDocument}
                disabled={!documentUrl}
              >
                View Lease
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="lease-note">
              Your landlord has sent this lease for your signature
              {lease.sentAt ? ` on ${formatLeaseTimestamp(lease.sentAt)}` : ''}. Please review it carefully
              before signing.
            </p>
            <div className="lease-actions">
              <button
                type="button"
                className="lease-btn lease-btn--primary"
                onClick={openModal}
                disabled={!documentUrl}
              >
                Review &amp; Sign
              </button>
              <button
                type="button"
                className="lease-btn lease-btn--secondary"
                onClick={handleOpenDocument}
                disabled={!documentUrl}
              >
                Open in New Tab
              </button>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Sign Lease Agreement"
        shouldCloseOnOverlayClick={!isSigning}
        ariaHideApp={false}
        className="lease-sign-modal"
        overlayClassName="lease-sign-modal-overlay"
      >
        <button
          type="button"
          className="lease-sign-modal-close"
          onClick={closeModal}
          aria-label="Close"
          disabled={isSigning}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h2 className="lease-sign-modal-title">{lease.title}</h2>
        <p className="lease-sign-modal-subtitle">Review the lease below, then sign with your full legal name.</p>

        {documentUrl && isPdfUrl(documentUrl) ? (
          <iframe src={documentUrl} title={lease.title} className="lease-sign-preview" />
        ) : (
          <div className="lease-sign-preview lease-sign-preview-fallback">
            <p className="lease-empty-text">
              This lease cannot be previewed here. Open it in a new tab to read it in full before signing.
            </p>
            <button
              type="button"
              className="lease-btn lease-btn--secondary"
              onClick={handleOpenDocument}
              disabled={!documentUrl}
            >
              Open Lease
            </button>
          </div>
        )}

        <div className="lease-field">
          <label className="lease-label" htmlFor="lease-signature-name">
            Type your full legal name
          </label>
          <input
            id="lease-signature-name"
            type="text"
            className={`lease-input${formError && !signatureName.trim() ? ' lease-input--error' : ''}`}
            value={signatureName}
            onChange={(e) => {
              setSignatureName(e.target.value);
              if (formError) setFormError('');
            }}
            placeholder="e.g. Jane A. Doe"
            maxLength={100}
            autoComplete="name"
          />
        </div>

        {signatureName.trim() ? (
          <div className="lease-signature-preview" aria-hidden="true">{signatureName.trim()}</div>
        ) : null}

        <label className="lease-sign-consent">
          <input
            type="checkbox"
            checked={hasAgreed}
            onChange={(e) => {
              setHasAgreed(e.target.checked);
              if (formError) setFormError('');
            }}
          />
          <span>
            I have read the lease agreement and agree to its terms. I understand that typing my name above
            constitutes my legally binding electronic signature.
          </span>
        </label>

        {formError ? <p className="lease-field-error" role="alert">{formError}</p> : null}

        <div className="lease-sign-modal-buttons">
          <button type="button" className="lease-btn lease-btn--secondary" onClick={closeModal} disabled={isSigning}>
            Cancel
          </button>
          <button type="button" className="lease-btn lease-btn--primary" onClick={handleSign} disabled={isSigning}>
            {isSigning ? 'Signing…' : 'Sign Lease'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default LeaseSignCard;
